/**
 * CAD Workbench - V2 Insole Pipeline Interface
 * 
 * Full pipeline: template selection → alignment → draping → boundary editing →
 * smoothing → approval → generation → validation → HIPAA sign-off → export.
 */

import React, { useState, useEffect } from 'react'
import { backendService } from '../../services/backendService'
import { formatAlignError } from '../../services/api/insoleV2Api'
import type {
  Template, AlignResponse, DrapeResponse,
  BoundaryApproveResponse, GenerateResponse, ValidateResponse,
  ExportFormat, OffsetDirection
} from '../../services/types'
import { logInfo, logError } from '../../utils/errorLogger'
import { PlanterView } from './components/PlanterView'
import { ConfidenceBadge } from './components/ConfidenceBadge'
import { TemplateSelector } from './components/TemplateSelector'
import { DrapeQualityPanel } from './components/DrapeQualityPanel'
import { SmoothTool } from './components/SmoothTool'
import { GenerationPanel } from './components/GenerationPanel'
import { ValidationDashboard } from './components/ValidationDashboard'
import { ApprovalSignOff } from './components/ApprovalSignOff'
import { ExportPanel } from './components/ExportPanel'
import { useUndoRedo } from './hooks/useUndoRedo'
import './CADWorkbench.css'

interface CADWorkbenchProps {
  projectId: string
  stlFileId: string
  onClose?: () => void
}

type WorkflowState =
  | 'idle' | 'loading-templates' | 'template-selected'
  | 'aligning' | 'aligned' | 'adjusting' | 'confirmed'
  | 'draping' | 'draped' | 'editing-boundary' | 'saving-boundary'
  | 'smoothing-boundary'
  | 'approving-boundary' | 'boundary-approved'
  | 'generating' | 'generated'
  | 'validating' | 'validated'
  | 'signing' | 'approved' | 'exporting'

export const CADWorkbench: React.FC<CADWorkbenchProps> = ({
  projectId,
  stlFileId,
  onClose
}) => {
  const [state, setState] = useState<WorkflowState>('loading-templates')
  const [templates, setTemplates] = useState<Template[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [alignmentData, setAlignmentData] = useState<AlignResponse | null>(null)
  const [drapeData, setDrapeData] = useState<DrapeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // Accumulated manual adjustments
  const [translation, setTranslation] = useState<[number, number]>([0, 0])
  const [rotation, setRotation] = useState<number>(0)
  const [scale, setScale] = useState<number>(1.0)

  // Smooth tool state
  const [isSmoothMode, setIsSmoothMode] = useState(false)
  const [smoothStartIndex, setSmoothStartIndex] = useState(-1)
  const [smoothEndIndex, setSmoothEndIndex] = useState(-1)
  const [smoothLoading, setSmoothLoading] = useState(false)

  // Post-boundary states
  const [approvedBoundary, setApprovedBoundary] = useState<BoundaryApproveResponse | null>(null)
  const [generateData, setGenerateData] = useState<GenerateResponse | null>(null)
  const [validateData, setValidateData] = useState<ValidateResponse | null>(null)
  const [approvalResult, setApprovalResult] = useState<{ audit_log_id: string; approved_at: string } | null>(null)
  const [exportResult, setExportResult] = useState<{ export_id: string; file_size_bytes: number; format: string } | null>(null)
  const [isOverrideApproval, setIsOverrideApproval] = useState(false)

  // Boundary editing with undo/redo
  const {
    state: controlPoints,
    set: setControlPoints,
    undo: undoControlPoints,
    redo: redoControlPoints,
    canUndo,
    canRedo,
    reset: resetControlPoints
  } = useUndoRedo<number[][]>([], 10)

  // Load templates on mount
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      setState('loading-templates')
      setError(null)
      
      const response = await backendService.listTemplates(1, 50)
      setTemplates(response.templates)
      
      if (response.templates.length > 0) {
        setState('idle')
        logInfo(`Loaded ${response.templates.length} templates`, 'CADWorkbench')
      } else {
        setError('No templates available')
        setState('idle')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates'
      logError(`Template loading failed: ${message}`, 'CADWorkbench')
      setError(message)
      setState('idle')
    }
  }

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template)
    setState('template-selected')
    logInfo(`Selected template: ${template.name}`, 'CADWorkbench')
  }

  const handleAlign = async () => {
    if (!selectedTemplate) {
      setError('No template selected')
      return
    }

    try {
      setState('aligning')
      setError(null)
      
      const response = await backendService.alignInsole({
        stl_file_id: stlFileId,
        template_id: selectedTemplate.id
      })
      
      setAlignmentData(response)
      setState('aligned')
      
      // Reset manual adjustments
      setTranslation([0, 0])
      setRotation(0)
      setScale(1.0)
      
      logInfo(`Alignment complete. Session ID: ${response.session_id}`, 'CADWorkbench')
    } catch (err) {
      const message = formatAlignError(err)
      logError(`Alignment error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('template-selected')
    }
  }

  const handleManualAdjustment = (newTranslation: [number, number], newRotation: number, newScale: number) => {
    setTranslation(newTranslation)
    setRotation(newRotation)
    setScale(newScale)
  }

  const handleConfirmAlignment = async () => {
    if (!alignmentData) {
      setError('No alignment data available')
      return
    }

    try {
      setState('adjusting')
      setError(null)
      
      // Send accumulated adjustments to backend
      const response = await backendService.adjustAlignment({
        session_id: alignmentData.session_id,
        translation,
        rotation,
        scale
      })
      
      // Update alignment data with adjusted template
      setAlignmentData({
        ...alignmentData,
        aligned_template_2d: response.aligned_template_2d
      })
      
      logInfo(`Alignment confirmed. Updated alignment ID: ${response.alignment_id}`, 'CADWorkbench')
      setState('confirmed')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm alignment'
      logError(`Confirmation error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('aligned')
    }
  }

  const handleDrape = async () => {
    if (!alignmentData) {
      setError('No alignment data available')
      return
    }

    try {
      setState('draping')
      setError(null)
      
      const response = await backendService.drapeTemplate({
        session_id: alignmentData.session_id,
        aligned_template_2d: alignmentData.aligned_template_2d,
        stl_file_id: stlFileId,
        algorithm: 'raycast'
      })
      
      setDrapeData(response)
      setState('draped')
      
      // Initialize control points from boundary curve
      resetControlPoints(response.boundary_curve_3d)
      
      logInfo(`Draping complete. Drape ID: ${response.drape_id}`, 'CADWorkbench')
      logInfo(`Quality: hit_rate=${response.quality_metrics.hit_rate.toFixed(3)}`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Draping failed'
      logError(`Draping error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('confirmed')
    }
  }

  const handleEditBoundary = () => {
    if (!drapeData) {
      setError('No boundary curve available')
      return
    }

    setState('editing-boundary')
    logInfo('Entered boundary editing mode', 'CADWorkbench')
  }

  const handleCancelEdit = () => {
    if (!drapeData) return

    // Reset to original boundary
    resetControlPoints(drapeData.boundary_curve_3d)
    setState('draped')
    logInfo('Cancelled boundary editing', 'CADWorkbench')
  }

  const handleSaveBoundary = async () => {
    if (!alignmentData || controlPoints.length === 0) {
      setError('No control points to save')
      return
    }

    try {
      setState('saving-boundary')
      setError(null)

      const response = await backendService.adjustBoundary({
        session_id: alignmentData.session_id,
        control_points: controlPoints
      })

      logInfo(`Boundary saved. Boundary ID: ${response.boundary_id}, Version: ${response.version}`, 'CADWorkbench')
      
      // Update drape data with new boundary
      if (drapeData) {
        setDrapeData({
          ...drapeData,
          boundary_curve_3d: response.control_points
        })
      }

      // Reset undo/redo history with saved points
      resetControlPoints(response.control_points)
      
      setState('draped')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save boundary'
      logError(`Boundary save error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('editing-boundary')
    }
  }

  const handleControlPointsChange = (newPoints: number[][]) => {
    setControlPoints(newPoints)
  }

  // -- Smooth tool handlers (#53) --
  const handleEnterSmoothMode = () => {
    setIsSmoothMode(true)
    setSmoothStartIndex(-1)
    setSmoothEndIndex(-1)
    setState('smoothing-boundary')
    logInfo('Entered smooth mode', 'CADWorkbench')
  }

  const handleSmoothRegionSelect = (index: number) => {
    if (smoothStartIndex < 0) {
      setSmoothStartIndex(index)
    } else if (smoothEndIndex < 0) {
      setSmoothEndIndex(index)
    } else {
      // Reset selection
      setSmoothStartIndex(index)
      setSmoothEndIndex(-1)
    }
  }

  const handleSmooth = async (iterations: number, lambda: number, preview: boolean) => {
    if (!alignmentData || smoothStartIndex < 0 || smoothEndIndex < 0) return

    const regionIndices: number[] = []
    if (smoothStartIndex <= smoothEndIndex) {
      for (let i = smoothStartIndex; i <= smoothEndIndex; i++) regionIndices.push(i)
    } else {
      for (let i = smoothStartIndex; i < controlPoints.length; i++) regionIndices.push(i)
      for (let i = 0; i <= smoothEndIndex; i++) regionIndices.push(i)
    }

    if (preview) {
      // For preview, just log — backend smooth is the real preview
      logInfo(`Preview smooth: ${regionIndices.length} points, iter=${iterations}, λ=${lambda}`, 'CADWorkbench')
    }

    try {
      setSmoothLoading(true)
      setError(null)

      const response = await backendService.smoothBoundary({
        session_id: alignmentData.session_id,
        region_indices: regionIndices,
        iterations,
        lambda
      })

      if (!preview) {
        setControlPoints(response.control_points)
        if (drapeData) {
          setDrapeData({ ...drapeData, boundary_curve_3d: response.control_points })
        }
        resetControlPoints(response.control_points)
        logInfo(`Smooth applied. Version: ${response.version}`, 'CADWorkbench')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to smooth boundary'
      logError(`Smooth error: ${message}`, 'CADWorkbench')
      setError(message)
    } finally {
      setSmoothLoading(false)
    }
  }

  const handleExitSmoothMode = () => {
    setIsSmoothMode(false)
    setSmoothStartIndex(-1)
    setSmoothEndIndex(-1)
    setState('editing-boundary')
  }

  // -- Boundary approval (#54) --
  const handleApproveBoundary = async () => {
    if (!alignmentData) return

    try {
      setState('approving-boundary')
      setError(null)

      const response = await backendService.approveBoundary({
        session_id: alignmentData.session_id
      })

      setApprovedBoundary(response)
      setState('boundary-approved')
      logInfo(`Boundary approved: ${response.approved_boundary_id}`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve boundary'
      logError(`Boundary approval error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('draped')
    }
  }

  const handleReEditBoundary = () => {
    setApprovedBoundary(null)
    setState('editing-boundary')
    logInfo('Re-editing boundary', 'CADWorkbench')
  }

  // -- Generate insole (#55) --
  const handleGenerate = async (params: {
    thickness_mm: number
    offset_direction: OffsetDirection
    edge_profile: string
    top_surface: string
  }) => {
    if (!alignmentData || !approvedBoundary) return

    try {
      setState('generating')
      setError(null)

      const response = await backendService.generateInsoleV2({
        session_id: alignmentData.session_id,
        approved_boundary_id: approvedBoundary.approved_boundary_id,
        ...params
      })

      setGenerateData(response)
      setState('generated')
      logInfo(`Insole generated: ${response.insole_mesh_id}`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Insole generation failed'
      logError(`Generation error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('boundary-approved')
    }
  }

  // -- Validate (#56) --
  const handleValidate = async () => {
    if (!generateData) return

    try {
      setState('validating')
      setError(null)

      const response = await backendService.validateInsole(generateData.insole_mesh_id)
      setValidateData(response)
      setState('validated')
      logInfo(`Validation: ${response.pre_screen_passed ? 'PASSED' : 'REVIEW'}`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Validation failed'
      logError(`Validation error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('generated')
    }
  }

  // -- Approval (#57 HIPAA) --
  const handleApproveExport = () => {
    setIsOverrideApproval(false)
    setState('signing')
  }

  const handleOverrideApprove = () => {
    setIsOverrideApproval(true)
    setState('signing')
  }

  const handleReturnToEdit = () => {
    setGenerateData(null)
    setValidateData(null)
    setApprovedBoundary(null)
    setState('editing-boundary')
  }

  const handleSignApproval = async (signature: string, notes: string) => {
    if (!generateData) return

    try {
      setState('signing')
      setError(null)

      const response = await backendService.approveInsole(
        generateData.insole_mesh_id,
        { doctor_signature: signature, notes: notes || undefined }
      )

      setApprovalResult({ audit_log_id: response.audit_log_id, approved_at: response.approved_at })
      setState('approved')
      logInfo(`Insole approved. Audit: ${response.audit_log_id}`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Approval failed'
      logError(`Approval error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('validated')
    }
  }

  // -- Export --
  const handleExport = async (format: ExportFormat) => {
    if (!generateData) return

    try {
      setState('exporting')
      setError(null)

      const response = await backendService.exportInsole(generateData.insole_mesh_id, format)
      setExportResult({ export_id: response.export_id, file_size_bytes: response.file_size_bytes, format: response.format })
      setState('approved')
      logInfo(`Exported: ${response.export_id} (${response.file_size_bytes} bytes)`, 'CADWorkbench')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed'
      logError(`Export error: ${message}`, 'CADWorkbench')
      setError(message)
      setState('approved')
    }
  }

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    if (state !== 'editing-boundary') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.shiftKey && e.key === 'z') {
          e.preventDefault()
          if (canRedo) redoControlPoints()
        } else if (e.key === 'z') {
          e.preventDefault()
          if (canUndo) undoControlPoints()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [state, canUndo, canRedo, undoControlPoints, redoControlPoints])

  const isLoading = ['loading-templates', 'aligning', 'adjusting', 'draping', 'saving-boundary', 'approving-boundary', 'generating', 'validating', 'exporting'].includes(state)
  const canAlign = state === 'template-selected' && selectedTemplate !== null
  const canConfirm = state === 'aligned' && alignmentData !== null
  const canDrape = state === 'confirmed' && alignmentData !== null
  const canEditBoundary = state === 'draped' && drapeData !== null
  const isEditingBoundary = state === 'editing-boundary' || state === 'smoothing-boundary'
  const isBoundaryApproved = state === 'boundary-approved' || state === 'generating' || state === 'generated' || state === 'validating' || state === 'validated' || state === 'signing' || state === 'approved' || state === 'exporting'
  const showPlanterView = alignmentData !== null && !['idle', 'loading-templates', 'template-selected', 'signing', 'approved'].includes(state)

  return (
    <div className="cad-workbench">
      <div className="workbench-header">
        <h2>CAD Workbench - V2 Insole Pipeline</h2>
        <div className="header-info">
          <span className="project-info">Project: {projectId}</span>
          <span className="stl-info">STL: {stlFileId.substring(0, 8)}...</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="close-button" disabled={isLoading}>
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
          <button onClick={() => setError(null)} className="error-dismiss">✕</button>
        </div>
      )}

      <div className="workbench-body">
        <div className="control-panel">
          {/* 1. Template Selection */}
          <div className="template-section">
            <h3>Template Selection</h3>
            <TemplateSelector
              templates={templates}
              selectedTemplate={selectedTemplate}
              onSelect={handleTemplateSelect}
              disabled={isLoading || state === 'aligned' || state === 'confirmed'}
            />
          </div>

          {/* 2. Alignment */}
          <div className="alignment-section">
            <h3>Alignment</h3>
            <button
              onClick={handleAlign}
              disabled={!canAlign || isLoading}
              className="align-button"
            >
              {state === 'aligning' ? 'Aligning...' : 'Align Template'}
            </button>

            {alignmentData && alignmentData.confidence_score !== null && (
              <div className="confidence-section">
                <label>Confidence:</label>
                <ConfidenceBadge score={alignmentData.confidence_score} />
              </div>
            )}

            {canConfirm && (
              <div className="confirm-section">
                <button onClick={handleConfirmAlignment} disabled={isLoading} className="confirm-button">
                  Confirm Alignment
                </button>
              </div>
            )}
            {state === 'adjusting' && (
              <div className="confirm-section">
                <button disabled className="confirm-button">Confirming...</button>
              </div>
            )}
          </div>

          {state === 'confirmed' && (
            <div className="success-banner">
              <span className="success-icon">✓</span>
              <span className="success-text">Alignment confirmed!</span>
            </div>
          )}

          {/* 3. Draping */}
          {(['confirmed', 'draping', 'draped', 'editing-boundary', 'saving-boundary', 'smoothing-boundary', 'approving-boundary', 'boundary-approved', 'generating', 'generated', 'validating', 'validated'].includes(state)) && (
            <div className="draping-section">
              <h3>Draping</h3>
              {canDrape && (
                <button onClick={handleDrape} disabled={isLoading} className="drape-button">
                  Drape Template
                </button>
              )}
              {state === 'draping' && (
                <button disabled className="drape-button">Draping...</button>
              )}
              {drapeData && <DrapeQualityPanel metrics={drapeData.quality_metrics} />}
            </div>
          )}

          {/* 4. Boundary Editing */}
          {canEditBoundary && (
            <div className="boundary-section">
              <h3>Boundary</h3>
              <div className="boundary-action-row">
                <button onClick={handleEditBoundary} disabled={isLoading} className="edit-boundary-button">
                  Edit Boundary
                </button>
                <button onClick={handleApproveBoundary} disabled={isLoading} className="approve-boundary-button">
                  Approve Boundary
                </button>
              </div>
            </div>
          )}

          {state === 'editing-boundary' && (
            <div className="boundary-editing-section">
              <h3>Boundary Editing</h3>
              <div className="editing-controls">
                <div className="undo-redo-controls">
                  <button onClick={undoControlPoints} disabled={!canUndo} className="undo-button" title="Undo (Ctrl+Z)">
                    ↶ Undo
                  </button>
                  <button onClick={redoControlPoints} disabled={!canRedo} className="redo-button" title="Redo (Ctrl+Shift+Z)">
                    ↷ Redo
                  </button>
                </div>
                <div className="save-controls">
                  <button onClick={handleEnterSmoothMode} disabled={isLoading} className="smooth-mode-button">
                    Smooth
                  </button>
                  <button onClick={handleCancelEdit} disabled={isLoading} className="cancel-button">
                    Cancel
                  </button>
                  <button onClick={handleSaveBoundary} disabled={isLoading} className="save-boundary-button">
                    {isLoading ? 'Saving...' : 'Save Boundary'}
                  </button>
                </div>
              </div>
              <div className="editing-help">
                <p>Drag control points to adjust boundary | Ctrl+Z undo, Ctrl+Shift+Z redo</p>
              </div>
            </div>
          )}

          {state === 'approving-boundary' && (
            <div className="boundary-section">
              <h3>Boundary</h3>
              <button disabled className="approve-boundary-button">Approving...</button>
            </div>
          )}

          {/* 5. Boundary Approved → Generation (#54/#55) */}
          {isBoundaryApproved && !(['signing', 'approved', 'exporting'].includes(state)) && (
            <div className="generation-section">
              <div className="success-banner">
                <span className="success-icon">✓</span>
                <span className="success-text">Boundary approved</span>
                <button onClick={handleReEditBoundary} className="re-edit-link">Re-edit</button>
              </div>

              {(state === 'boundary-approved' || state === 'generating') && (
                <GenerationPanel
                  onGenerate={handleGenerate}
                  isLoading={state === 'generating'}
                />
              )}

              {generateData && (state === 'generated' || state === 'validating' || state === 'validated') && (
                <>
                  <div className="success-banner">
                    <span className="success-icon">✓</span>
                    <span className="success-text">Insole generated</span>
                  </div>

                  {state === 'generated' && (
                    <button onClick={handleValidate} disabled={isLoading} className="validate-button">
                      Run Validation
                    </button>
                  )}

                  {state === 'validating' && (
                    <button disabled className="validate-button">Validating...</button>
                  )}

                  {/* 6. Validation Dashboard (#56) */}
                  {(state === 'validated' || validateData) && (
                    <ValidationDashboard
                      validation={validateData}
                      report={generateData.validation_report}
                      metrics={generateData.metrics}
                      onApproveExport={handleApproveExport}
                      onOverrideApprove={handleOverrideApprove}
                      onReturnToEdit={handleReturnToEdit}
                      isLoading={isLoading}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* 7. HIPAA Sign-off (#57) */}
          {(state === 'signing' || state === 'approved') && alignmentData && generateData && (
            <ApprovalSignOff
              meshId={generateData.insole_mesh_id}
              sessionId={alignmentData.session_id}
              projectId={projectId}
              validationReport={validateData?.validation_report ?? generateData.validation_report}
              isOverride={isOverrideApproval}
              onApprove={handleSignApproval}
              onCancel={() => setState('validated')}
              isLoading={false}
              approvalResult={approvalResult}
            />
          )}

          {/* 8. Export */}
          {state === 'approved' && approvalResult && generateData && (
            <ExportPanel
              onExport={handleExport}
              isLoading={isLoading}
              exportResult={exportResult}
            />
          )}
        </div>

        <div className="viewer-panel">
          {showPlanterView && alignmentData ? (
            <>
              <PlanterView
                projectId={projectId}
                stlFileId={stlFileId}
                alignedTemplate={alignmentData.aligned_template_2d}
                plantarProjection={alignmentData.plantar_projection}
                boundaryCurve3d={drapeData?.boundary_curve_3d}
                onManualAdjust={handleManualAdjustment}
                controlPoints={isEditingBoundary ? controlPoints : null}
                onControlPointsChange={handleControlPointsChange}
                isEditingBoundary={isEditingBoundary}
                isSmoothMode={isSmoothMode}
                onSmoothRegionSelect={handleSmoothRegionSelect}
                smoothStartIndex={smoothStartIndex}
                smoothEndIndex={smoothEndIndex}
              />
              {state === 'smoothing-boundary' && (
                <SmoothTool
                  startIndex={smoothStartIndex}
                  endIndex={smoothEndIndex}
                  totalPoints={controlPoints.length}
                  onSmooth={handleSmooth}
                  onCancel={handleExitSmoothMode}
                  isLoading={smoothLoading}
                />
              )}
            </>
          ) : state === 'signing' || state === 'approved' ? (
            <div className="viewer-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📋</div>
                <p>Review and sign the approval form</p>
              </div>
            </div>
          ) : (
            <div className="viewer-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">📐</div>
                <p>Select a template and click &quot;Align Template&quot; to begin</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
